import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OfferingGroup, OfferingType, OfferingGroupWithTypes } from '@/lib/producer/offeringTypes';
import { useMemo } from 'react';

/**
 * Fetches the canonical offering catalog (groups + active types)
 * and returns them grouped for UI rendering.
 *
 * This is the single source of truth for offering vocabulary in the frontend.
 * Do NOT duplicate this data as hardcoded constants.
 */
export function useOfferingCatalog() {
  const groupsQuery = useQuery({
    queryKey: ['offering-groups'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('offering_groups')
        .select('key, label_sv, description_sv, sort_order, is_active')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as OfferingGroup[];
    },
  });

  const typesQuery = useQuery({
    queryKey: ['offering-types-active'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('offering_types')
        .select('key, group_key, label_sv, short_description_sv, sort_order, is_active, supports_note')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as OfferingType[];
    },
  });

  const grouped = useMemo<OfferingGroupWithTypes[]>(() => {
    if (!groupsQuery.data || !typesQuery.data) return [];
    return groupsQuery.data.map((g) => ({
      ...g,
      // Filter out the generic 'other' type — GroupedOfferingSelector renders
      // its own dedicated "Något annat" free-text field instead.
      types: typesQuery.data.filter((t) => t.group_key === g.key && t.key !== 'other'),
    })).filter((g) => g.types.length > 0);
  }, [groupsQuery.data, typesQuery.data]);

  return {
    groups: grouped,
    allTypes: typesQuery.data ?? [],
    isLoading: groupsQuery.isLoading || typesQuery.isLoading,
  };
}
